import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useRouteMatch } from 'react-router-dom';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import ButtonBase from '@material-ui/core/ButtonBase';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableFooter from '@material-ui/core/TableFooter';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import queryString from 'query-string';

import AddRuleDrawer from '~/components/AddRuleDrawer';
import CardBase from '~/components/CardBase';
import EditImportedTransactionRuleDialog from '~/components/EditImportedTransactionRuleDialog';
import EmptyState from '~/components/EmptyState';
import PageGrid from '~/components/PageGrid';
import PageHeader from '~/components/PageHeader';
import { PersonContext } from '~/contexts/PersonContext';
import { find } from '~/feathersFunctionalWrapper';
import { useSegmentTrack } from '~/functions/SegmentFunctions';
import history from '~/history';

import FilterRulesDialog from './FilterRulesDialog';
import { useStyles } from './styles';

const PAGE_SIZE = 20;

export default function ImportedTransactionRules() {
  const classes = useStyles();
  const location = useLocation();
  const match = useRouteMatch();
  const tracking = useSegmentTrack();
  const { organizationId } = useContext(PersonContext);

  const initialFilter = {
    page: 0,
    descriptionContains: null,
    type: null,
    amount: null,
    analysisAccount: null,
    destinationAccount: null,
  };

  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editRuleId, setEditRuleId] = useState(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filter, setFilter] = useState(initialFilter);
  const [rules, setRules] = useState({ data: [], total: 0 });

  const isFilterActive = useMemo(() => {
    return !!(
      filter.descriptionContains ||
      filter.type ||
      filter.amount ||
      filter.analysisAccount ||
      filter.destinationAccount
    );
  }, [filter]);

  const actionButtons = () => [
    {
      text: 'Add Rule',
      action: () => {
        tracking('add_rule clicked', 'Imported Transaction Rules', {
          organizationId,
        });
        setAddDialog(true);
      },
      class: 'add',
    },
    {
      text: 'Filter',
      action: () => {
        tracking('filter clicked', 'Imported Transaction Rules', {
          organizationId,
        });
        setIsFilterDialogOpen(true);
      },
      class: 'filter',
    },
  ];

  const getRules = useCallback(
    async (page) => {
      setLoading(true);
      const query = {
        organizationId,
        $limit: PAGE_SIZE,
        $skip: page * PAGE_SIZE,
        $sort: {
          descriptionContains: 1,
          id: 1,
        },
      };

      if (filter.descriptionContains) {
        query.descriptionContains = { $iLike: `%${filter.descriptionContains}%` };
      }
      if (filter.type) {
        query.type = filter.type.id;
      }
      if (filter.amount) {
        query.transactionAmount = filter.amount;
      }
      if (filter.analysisAccount) {
        query.analysisAccountId = filter.analysisAccount.id;
      }
      if (filter.destinationAccount) {
        query.destinationAccountId = filter.destinationAccount.id;
      }
      const result = await find('imported-transaction-rules', { query });

      setRules(result);
      setLoading(false);
    },
    [
      filter.amount,
      filter.analysisAccount,
      filter.descriptionContains,
      filter.type,
      filter.destinationAccount,
      organizationId,
    ],
  );

  const closeDialogs = () => {
    const parsedQuery = queryString.parse(location.search);
    if (parsedQuery.addRule) {
      history.replace(window.location.pathname);
    }
    setAddDialog(false);
    setEditDialog(false);
    setEditRuleId(null);
  };

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    if (parsedQuery.addRule) {
      setAddDialog(true);
    }
  }, [location.search]);

  const handleChangePage = async (page) => {
    setFilter((prevFilter) => ({ ...prevFilter, page }));
  };

  useEffect(() => {
    getRules(filter.page);
  }, [getRules, filter]);

  if (loading) {
    return null;
  }

  return (
    <>
      <FilterRulesDialog
        isOpen={isFilterDialogOpen}
        closeDialog={() => setIsFilterDialogOpen(false)}
        filter={filter}
        updateFilter={setFilter}
      />
      <PageGrid>
        <PageHeader match={match} title="Imported Transaction Rules" actionButtons={actionButtons()} />
        {addDialog && <AddRuleDrawer onAdd={() => getRules(filter.page)} onClose={closeDialogs} />}
        <EditImportedTransactionRuleDialog
          isOpen={editDialog}
          closeDialog={closeDialogs}
          onEdit={() => getRules(filter.page)}
          onDelete={() => getRules(filter.page)}
          ruleId={editRuleId}
        />
        {rules.total === 0 && !isFilterActive && (
          <EmptyState
            icon="/icons/no_data_empty_state_icon.svg"
            title="Automate how your transactions are categorized"
            body={
              <Typography variant="body1" align="center">
                Create rules to speed up your bookkeeping and reduce manual entry. Automatically assign categories,
                properties, and accounts to matching transactions.
              </Typography>
            }
            buttonProps={[
              {
                color: 'purple',
                variant: 'contained',
                text: 'Add Rule',
                onClickFunction: () => setAddDialog(true),
              },
            ]}
          />
        )}
        {rules.total === 0 && isFilterActive && (
          <EmptyState
            icon="/icons/user_action_empty_state_icon.svg"
            title="No rules found"
            body={
              <Typography variant="body1" align="center">
                Try adjusting your filters or search keywords. Still not seeing what you need? You can create a new rule
                anytime.
              </Typography>
            }
            buttonProps={[
              {
                color: 'purple',
                variant: 'contained',
                text: 'Add Rule',
                onClickFunction: () => setAddDialog(true),
              },
              {
                color: 'purple',
                variant: 'outlined',
                text: 'Clear Filters',
                onClickFunction: () => {
                  setFilter(initialFilter);
                },
              },
            ]}
          />
        )}
        {rules.total > 0 && (
          <Grid item xs={12}>
            <CardBase>
              <CardContent>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography variant="subtitle2">Description</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">Type</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">Amount</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">Imported From Account</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">Booked To Account</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rules.data.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell>
                          <ButtonBase
                            className={classes.textButton}
                            onClick={() => {
                              setEditRuleId(rule.id);
                              setEditDialog(true);
                            }}
                          >
                            <Typography variant="body2" component="div" className={classes.leftAlign}>
                              {rule.descriptionContains || '---'}
                            </Typography>
                          </ButtonBase>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" component="div">
                            {rule.type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {rule.transactionAmount !== null ? (
                            <NumberFormat
                              displayType="text"
                              value={rule.transactionAmount}
                              thousandSeparator
                              prefix="$"
                              decimalScale={2}
                              fixedDecimalScale
                            />
                          ) : (
                            '---'
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" component="div">
                            {rule.analysisAccountId ? rule.analysisAccount.name : '---'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" component="div">
                            {rule.destinationAccountId ? rule.destinationAccount.name : '---'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        variant="footer"
                        count={rules.total}
                        page={filter.page}
                        rowsPerPage={PAGE_SIZE}
                        onChangePage={(_event, newPage) => {
                          handleChangePage(newPage);
                        }}
                        rowsPerPageOptions={[PAGE_SIZE]}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </CardContent>
            </CardBase>
          </Grid>
        )}
      </PageGrid>
    </>
  );
}
